import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Checkbox,
    Chip,
    Autocomplete,
    MenuList,
    ClickAwayListener,
    Popper,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    QuestionAnswer as VerifyIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import { useParams, useLocation } from 'react-router-dom';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import PTProviderService from '../../../services/testingPTProviderService';
import UnfreezeApiService from '../../../services/UnfreezeApiService';
import {
    insertDANC,
    updateDANCBySourceId,
    getDANC
} from '../../../services/apiCalibration';
import { getScrutinyRemarks, getClauseNo } from '../../../services/api';
// Adequacy Check Component with DA NC Integration on Submit
const AdequacyCheckSection = React.memo(({
    sectionId,
    title,
    applicationStage,
    orgId,
    appId,
    adequacyCheck,
    onAdequacyChange,
    onRemarksChange,
    onSubmit,
    loading,
    currentUser = 'System User',
    currentUserId = 0,
    insertDANC,
    updateDANC,
    refreshTrigger = 0,
    selectedItems = [],
    dancRecords = [],
    clauseNo = [],
    selectedClause,
    onClauseChange
}) => {
    // Local state for DA NC operations
    const [dancLoading, setDancLoading] = useState(false);
    const [dancError, setDancError] = useState('');
    const [dancSuccess, setDancSuccess] = useState('');
    const [existingDANCRecord, setExistingDANCRecord] = useState(null);
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [loadingRemarks, setLoadingRemarks] = useState(false);
    const [scrutinyRemarksLoading, setScrutinyRemarksLoading] = useState(false);

    // State for checkbox dropdown
    const [selectedRemarksOptions, setSelectedRemarksOptions] = useState([]);
    const [isCheckboxDropdownOpen, setIsCheckboxDropdownOpen] = useState(false);

    // Load existing DA NC data based on selected items and their sourceid match
    useEffect(() => {
        const loadExistingDANCData = async () => {
            if (!selectedItems.length) {
                // Clear existing record if no items selected
                setExistingDANCRecord(null);
                onAdequacyChange(sectionId, '');
                onRemarksChange(sectionId, '');
                return;
            }

            try {
                setDancLoading(true);
                console.log(`Analyzing selected items for batch operation:`, selectedItems);

                // Analyze selected items and their current statuses
                const itemStatuses = selectedItems.map(itemId => {
                    const dancRecord = dancRecords.find(record =>
                        record.sourceid && parseInt(record.sourceid) === parseInt(itemId)
                    );
                    return {
                        itemId,
                        status: dancRecord ? dancRecord.InformationStatus : 'Not Assessed',
                        record: dancRecord
                    };
                });

                console.log('Selected items analysis:', itemStatuses);

                // Get unique statuses from selected items
                const uniqueStatuses = [...new Set(itemStatuses.map(item => item.status))];
                const hasMultipleStatuses = uniqueStatuses.length > 1;

                // For batch operations, show info about what will happen
                if (hasMultipleStatuses) {
                    const adequate = itemStatuses.filter(item => item.status === 'Adequate').length;
                    const inadequate = itemStatuses.filter(item => item.status === 'Inadequate').length;
                    const notAssessed = itemStatuses.filter(item => item.status === 'Not Assessed').length;

                    let statusMessage = `Selected ${selectedItems.length} items: `;
                    const statusCounts = [];
                    if (adequate > 0) statusCounts.push(`${adequate} Adequate`);
                    if (inadequate > 0) statusCounts.push(`${inadequate} Inadequate`);
                    if (notAssessed > 0) statusCounts.push(`${notAssessed} Not Assessed`);

                    setDancSuccess(statusMessage + statusCounts.join(', ') + '. Choose new status to apply to all selected items.');
                    setExistingDANCRecord(null); // No single record to bind
                } else if (uniqueStatuses[0] !== 'Not Assessed') {
                    // All selected items have the same assessed status
                    const firstRecord = itemStatuses[0].record;
                    setExistingDANCRecord(firstRecord);

                    // Bind the common status and remarks
                    onAdequacyChange(sectionId, uniqueStatuses[0]);
                    const remarks = firstRecord ? (firstRecord.InformationProvided || firstRecord.Observation || '') : '';
                    onRemarksChange(sectionId, remarks);

                    setDancSuccess(`All ${selectedItems.length} selected items have status: ${uniqueStatuses[0]}`);

                } else {
                    // All selected items are "Not Assessed"
                    setExistingDANCRecord(null);
                    onAdequacyChange(sectionId, '');
                    onRemarksChange(sectionId, '');
                    setDancSuccess(`Selected ${selectedItems.length} items are not assessed. Choose status to apply to all.`);
                }

            } catch (error) {
                console.log('Error analyzing selected items:', error.message);
                setDancError(`Error loading DA-NC data: ${error.message}`);
                setExistingDANCRecord(null);
            } finally {
                setDancLoading(false);
            }
        };

        loadExistingDANCData();
    }, [selectedItems, dancRecords, sectionId, refreshTrigger, onAdequacyChange, onRemarksChange]);

    // Fetch scrutiny remarks from API
    const fetchScrutinyRemarks = useCallback(async () => {
        try {
            setScrutinyRemarksLoading(true);
            console.log('Fetching scrutiny remarks for Proficiency Testing section...');

            const documentRemarksApi = await getScrutinyRemarks({
                sectionName: 'Proficiency Testing',
                type: localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review'
            });

            const documentRemarks = documentRemarksApi.data;
            if (documentRemarks.success && documentRemarks.data) {
                setScrutinyRemarks(documentRemarks.data);
                console.log('Loaded scrutiny remarks:', documentRemarks.data.length);
            } else {
                console.warn('No scrutiny remarks found or API returned success: false');
                setScrutinyRemarks([]);
            }
        } catch (error) {
            console.error('Error fetching scrutiny remarks:', error);
            setScrutinyRemarks([]);
        } finally {
            setScrutinyRemarksLoading(false);
        }
    }, []);


    // Load scrutiny remarks for adequacy section
    useEffect(() => {
        const fetchScrutinyRemarks = async () => {
            try {
                setLoadingRemarks(true);
                const type = localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review';
                const documentRemarksApi = await getScrutinyRemarks({ sectionName: title, type });
                const documentRemarks = documentRemarksApi.data;
                if (documentRemarks.success && documentRemarks.data) {
                    // Use the API response data directly as it already has the correct structure
                    setScrutinyRemarks(documentRemarks.data);
                    console.log('AdequacyCheckSection: Loaded scrutiny remarks:', documentRemarks.data.length);
                } else {
                    setScrutinyRemarks([]);
                }
            } catch (error) {
                console.error('AdequacyCheckSection: Error fetching scrutiny remarks:', error);
                setScrutinyRemarks([]);
            } finally {
                setLoadingRemarks(false);
            }
        };

        fetchScrutinyRemarks();
    }, [title]);

    // Clear messages after timeout
    useEffect(() => {
        if (dancError || dancSuccess) {
            const timer = setTimeout(() => {
                setDancError('');
                setDancSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [dancError, dancSuccess]);

    // Don't render if not in adequacy check stage or user type (allow both userTypeId 4 and 6)
    const userTypeId = localStorage.getItem('userTypeId');
    if ((applicationStage !== "18" && applicationStage !== "18b" && applicationStage !== "20") || (userTypeId !== '4' && userTypeId !== '6')) return null;

    const adequacy = adequacyCheck || { status: '', remarks: '' };

    // Standard status change handler (no DA NC calls)
    const handleStatusChange = (status) => {
        onAdequacyChange(sectionId, status);
    };

    // Standard remarks change handler
    const handleRemarksChange = (remarks) => {
        onRemarksChange(sectionId, remarks);
    };

    // Handle checkbox selection for predefined remarks
    const handleCheckboxChange = (option, checked) => {
        let newSelectedOptions;
        if (checked) {
            newSelectedOptions = [...selectedRemarksOptions, option];
        } else {
            newSelectedOptions = selectedRemarksOptions.filter(item =>
                item.inadequate_remarks !== option.inadequate_remarks
            );
        }
        setSelectedRemarksOptions(newSelectedOptions);

        // Update remarks textbox with comma-separated values
        const remarksText = newSelectedOptions
            .map(item => typeof item === 'string' ? item : item.inadequate_remarks)
            .join(', ');
        handleRemarksChange(remarksText);
    };

    // Clear all selected checkboxes
    const handleClearAllCheckboxes = () => {
        setSelectedRemarksOptions([]);
        handleRemarksChange('');
    };

    // Enhanced submit handler - delegate to main component
    const handleSubmit = async () => {
        try {


            setDancLoading(true);
            setDancError('');
            setDancSuccess('');

            console.log('AdequacyCheckSection: Delegating to main component submit handler');
            // Delegate to the main component's handleAdequacySubmit which has PT Provider context
            await onSubmit(sectionId);

            setDancSuccess('Adequacy check submitted successfully');
            window.location.reload();
        } catch (error) {
            console.error('Error submitting adequacy check:', error);
            setDancError('Error submitting adequacy check: ' + error.message);
        } finally {
            setDancLoading(false);

        }
    };

    return (
        <Box sx={{
            mb: 3,
            p: 3,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '2px solid #e9ecef',
            borderLeft: '4px solid #007bff'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: '1.1rem'
                }}>
                    📋 Adequacy Check - {title}
                </Typography>


                {dancLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption">Loading DA NC data...</Typography>
                    </Box>
                )}
            </Box>

            {/* DA NC Error/Success Messages */}
            {dancError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDancError('')}>
                    {dancError}
                </Alert>
            )}

            {dancSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDancSuccess('')}>
                    {dancSuccess}
                </Alert>
            )}

            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: 2,
                            '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                            },
                            '&.Mui-focused': {
                                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                            }
                        }
                    }}>
                        <InputLabel>Document Status</InputLabel>
                        <Select
                            value={adequacy.status}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            label="Document Status"
                        >
                            <MenuItem value="">-- Select Status --</MenuItem>
                            <MenuItem value="Adequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#28a745', fontSize: '1rem' }} />
                                    Adequate
                                </Box>
                            </MenuItem>
                            <MenuItem value="Inadequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
                                    Inadequate
                                </Box>
                            </MenuItem>
                            {userTypeId === '4' && (
                                <MenuItem value="To be Verify">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <VerifyIcon sx={{ color: '#35dcdcff', fontSize: '1rem' }} />
                                        To be Verify
                                    </Box>
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Grid>

                {(adequacy.status === 'Inadequate' && userTypeId === '6') && (
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            fullWidth
                            options={clauseNo}
                            value={selectedClause}
                            onChange={(event, newValue) => {
                                onClauseChange && onClauseChange(newValue);
                            }}
                            getOptionLabel={(option) => {
                                if (!option) return '';
                                if (typeof option === 'string') return option;
                                return option.DisplayName || option.clause_no || option.text || '';
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Clause No."
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                            borderRadius: 2,
                                            '&:hover': {
                                                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                                            },
                                            '&.Mui-focused': {
                                                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                )}

                {adequacy.status === 'Inadequate' && (
                    <>
                        <Grid item xs={12} md={8}>
                            {/* Simple textarea for remarks */}
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Remarks (Required)"
                                placeholder="Type your detailed remarks here. Use the dropdown below to select predefined remarks."
                                value={adequacy.remarks || ''}
                                onChange={(e) => handleRemarksChange(e.target.value)}
                                required
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(220,53,69,0.15)',
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: '0 4px 12px rgba(220,53,69,0.25)',
                                        }
                                    }
                                }}
                            />

                            {/* Predefined Remarks Dropdown */}
                            {scrutinyRemarks.length > 0 && (
                                <Box sx={{ mt: 2, position: 'relative' }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setIsCheckboxDropdownOpen(!isCheckboxDropdownOpen)}
                                        sx={{
                                            mb: 1,
                                            borderColor: '#007bff',
                                            color: '#007bff',
                                            '&:hover': { borderColor: '#0056b3', backgroundColor: '#f8f9ff' }
                                        }}
                                    >
                                        {isCheckboxDropdownOpen ? 'Hide' : 'Show'} Predefined Remarks ({scrutinyRemarks.length} available)
                                    </Button>

                                    {selectedRemarksOptions.length > 0 && (
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={handleClearAllCheckboxes}
                                            sx={{ ml: 1, color: '#dc3545' }}
                                        >
                                            Clear All ({selectedRemarksOptions.length})
                                        </Button>
                                    )}

                                    {isCheckboxDropdownOpen && (
                                        <Paper
                                            elevation={3}
                                            sx={{
                                                mt: 1,
                                                maxHeight: 300,
                                                overflow: 'auto',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 2
                                            }}
                                        >
                                            <MenuList dense>
                                                {scrutinyRemarks.map((option, index) => {
                                                    const remarkText = typeof option === 'string' ? option : option.inadequate_remarks;
                                                    const isSelected = selectedRemarksOptions.some(
                                                        item => (typeof item === 'string' ? item : item.inadequate_remarks) === remarkText
                                                    );

                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            onClick={() => handleCheckboxChange(option, !isSelected)}
                                                            sx={{
                                                                py: 1,
                                                                px: 2,
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                minHeight: 'auto'
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={isSelected}
                                                                size="small"
                                                                sx={{ p: 0, mr: 2 }}
                                                            />
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.875rem',
                                                                        lineHeight: 1.4,
                                                                        wordBreak: 'break-word',
                                                                        whiteSpace: 'pre-wrap'
                                                                    }}
                                                                >
                                                                    {remarkText}
                                                                </Typography>
                                                                {typeof option !== 'string' && option.type && (
                                                                    <Chip
                                                                        label={option.type}
                                                                        size="small"
                                                                        color={option.type === 'Assessment' ? 'primary' : 'secondary'}
                                                                        variant="outlined"
                                                                        sx={{
                                                                            fontSize: '0.7rem',
                                                                            height: '18px',
                                                                            mt: 0.5
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </MenuItem>
                                                    );
                                                })}
                                            </MenuList>
                                        </Paper>
                                    )}
                                </Box>
                            )}

                            <Typography variant="caption" sx={{
                                color: '#6c757d',
                                display: 'block',
                                mt: 1,
                                fontStyle: 'italic'
                            }}>
                                {loadingRemarks
                                    ? "Loading existing remarks..."
                                    : scrutinyRemarks.length > 0
                                        ? `${scrutinyRemarks.length} predefined remark(s) available for "${title}". You can select multiple options from the dropdown above.`
                                        : "No predefined remarks found for this section. Type your detailed remarks in the text area above."
                                }
                            </Typography>
                        </Grid>
                    </>
                )}
            </Grid>

            {adequacy.status && (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleSubmit}
                            disabled={
                                !adequacy.status ||
                                (adequacy.status === 'Inadequate' && (!adequacy.remarks || adequacy.remarks.toString().trim() === '')) ||
                                loading ||
                                dancLoading
                            }
                            sx={{
                                background: existingDANCRecord
                                    ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' // Blue gradient for update
                                    : (adequacy.status === 'Adequate'
                                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                                        : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'),
                                color: 'white',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                                '&:disabled': {
                                    background: '#e9ecef',
                                    color: '#6c757d',
                                    transform: 'none',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {(loading || dancLoading) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: 'white' }} />
                                    Processing Batch...
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {(() => {
                                        const itemStatuses = selectedItems.map(itemId => {
                                            const dancRecord = dancRecords.find(record =>
                                                record.sourceid && parseInt(record.sourceid) === parseInt(itemId)
                                            );
                                            return dancRecord ? dancRecord.InformationStatus : 'Not Assessed';
                                        });

                                        const toUpdate = itemStatuses.filter(status => status !== 'Not Assessed').length;
                                        const toInsert = itemStatuses.filter(status => status === 'Not Assessed').length;

                                        if (toUpdate > 0 && toInsert > 0) {
                                            return <>🔄📤 Update & Create ({selectedItems.length} items)</>;
                                        } else if (toUpdate > 0) {
                                            return <>🔄 Update ({toUpdate} items)</>;
                                        } else {
                                            return <>📤 Create ({toInsert} items)</>;
                                        }
                                    })()}
                                </Box>
                            )}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
});

const ProficiencyTestingSection = () => {
    const { orgId: urlOrgId } = useParams();
    const location = useLocation();

    const [participationIn, setParticipationIn] = useState('0');
    const [nodalLaboratory, setNodalLaboratory] = useState('');
    const [nodalLaboratoryOthers, setNodalLaboratoryOthers] = useState('');
    const [ilcDetails, setIlcDetails] = useState('');
    const [remarks, setRemarks] = useState('');

    // Scrutiny Remarks states
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [scrutinyRemarksLoading, setScrutinyRemarksLoading] = useState(false);
    const [isDropdownSelection, setIsDropdownSelection] = useState(false);
    

    // Function to get orgId from URL query parameters with decryption
    const getOrgIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('📍 ProficiencyTestingSection - Decrypted orgId from URL query:', decryptedOrgId);
                console.log('📍 ProficiencyTestingSection - Original encrypted orgId:', encryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('⚠️ ProficiencyTestingSection - Failed to decrypt orgId, using original:', encryptedOrgId, error);
                return encryptedOrgId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('📍 ProficiencyTestingSection - Decrypted appId from URL query:', decryptedAppId);
                console.log('📍 ProficiencyTestingSection - Original encrypted appId:', encryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('⚠️ ProficiencyTestingSection - Failed to decrypt appId, using original:', encryptedAppId, error);
                return encryptedAppId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get assessmentId from URL query parameters with decryption
    const getAssessmentIdFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessmentId = searchParams.get('assessmentId');
        if (encryptedAssessmentId) {
            try {
                const decryptedAssessmentId = decryptText(encryptedAssessmentId);
                console.log('📍 ProficiencyTestingSection - Decrypted assessmentId from URL query:', decryptedAssessmentId);
                return decryptedAssessmentId;
            } catch (error) {
                console.log('⚠️ ProficiencyTestingSection - Failed to decrypt assessmentId:', error);
                return encryptedAssessmentId;
            }
        }
        return null;
    };

    // Function to get assessorId from URL query parameters with decryption
    const getAssessorIdFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessorId = searchParams.get('assessorId');
        if (encryptedAssessorId) {
            try {
                const decryptedAssessorId = decryptText(encryptedAssessorId);
                console.log('📍 ProficiencyTestingSection - Decrypted assessorId from URL query:', decryptedAssessorId);
                return decryptedAssessorId;
            } catch (error) {
                console.log('⚠️ ProficiencyTestingSection - Failed to decrypt assessorId:', error);
                return encryptedAssessorId;
            }
        }
        return null;
    };

    const getAssessmentId = () => {
        return getAssessmentIdFromQuery();
    };

    const getAssessorId = () => {
        return getAssessorIdFromQuery();
    };

    // Get assessmentId and assessorId for userType 6 (Assessment)
    const assessmentId = getAssessmentId();
    const assessorId = getAssessorId();

    // Function to get orgId with priority: isSubUser check > URL query (encrypted) > URL params > null
    const getOrgId = useCallback(() => {


        // First check URL query parameters (encrypted) - highest priority
        const queryOrgId = getOrgIdFromQuery();
        if (queryOrgId) {
            console.log('📍 ProficiencyTestingSection - Using decrypted orgId from URL query:', queryOrgId);
            return queryOrgId;
        }

        // Then check URL parameters (unencrypted)
        if (urlOrgId) {
            console.log('📍 ProficiencyTestingSection - Using orgId from URL params:', urlOrgId);
            return urlOrgId;
        }

        // No fallback - orgId must be provided via URL
        console.log('⚠️ ProficiencyTestingSection - No orgId found in URL parameters');
        return null;
    }, [getOrgIdFromQuery, urlOrgId]);

    // Function to get appId with priority: isSubUser check > URL query (encrypted) only
    const getAppId = useCallback(() => {

        // Only check URL query parameters (encrypted)
        const queryAppId = getAppIdFromQuery();
        if (queryAppId) {
            console.log('📍 ProficiencyTestingSection - Using decrypted appId from URL query:', queryAppId);
            return queryAppId;
        }

        // No fallback - appId must be provided via URL query parameters
        console.log('⚠️ ProficiencyTestingSection - No appId found in URL query parameters');
        return null;
    }, [getAppIdFromQuery]);

    const [orgId, setOrgId] = useState(() => {
        // Initialize with query parameters first, then URL params
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('🚀 ProficiencyTestingSection - Initial orgId from encrypted query:', decryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('Failed to decrypt initial orgId, using original:', encryptedOrgId);
                return encryptedOrgId;
            }
        }
        // Only fallback to URL params, no localStorage
        return urlOrgId || null;
    });

    const [appId, setAppId] = useState(() => {
        // Initialize with query parameters only
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('🚀 ProficiencyTestingSection - Initial appId from encrypted query:', decryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('Failed to decrypt initial appId, using original:', encryptedAppId);
                return encryptedAppId;
            }
        }
        // No fallback - must be provided via query parameters
        return null;
    });

    // Update orgId state only (no localStorage)
    const updateOrgId = (newOrgId) => {
        console.log('Updating orgId state:', newOrgId);
        setOrgId(newOrgId);
    };

    // Update appId state only (no localStorage)
    const updateAppId = (newAppId) => {
        console.log('Updating appId state:', newAppId);
        setAppId(newAppId);
    };

    // API data states
    const [nablProviders, setNablProviders] = useState([]);
    const [otherProviders, setOtherProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    // Log save messages for debugging
    useEffect(() => {
        if (saveMessage) {
            console.log('💾 ProficiencyTestingSection - Save message:', saveMessage);
        }
    }, [saveMessage]);
    const [existingDataId, setExistingDataId] = useState(null);
    const [savedData, setSavedData] = useState([]);
    const [loadingTable, setLoadingTable] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [deleteItemType, setDeleteItemType] = useState(''); // 'form' or 'table'
    const [applicationStage, setApplicationStage] = useState(null);
    const [iqcType, setIqcType] = useState("");

    const iqcOptions = [
  "Use of reference materials or quality control materials",
  "Use of alternative instrumentation that has been calibrated to provide traceable results",
  "Functional check(s) of measuring and testing equipment",
  "Use of check or working standards with control charts, where applicable",
  "Intermediate checks on measuring equipment",
  "Replicate tests or calibrations using the same or different methods",
  "Retesting or recalibration of retained items",
  "Correlation of results for different characteristics of an item",
  "Review of reported results",
  "Intralaboratory comparisons",
  "Testing of blind sample(s)",
];

    // useEffect to handle parameter updates from URL
    useEffect(() => {
        const currentOrgId = getOrgId();
        if (currentOrgId !== orgId) {
            updateOrgId(currentOrgId);
        }

        const currentAppId = getAppId();
        if (currentAppId !== appId) {
            console.log('ProficiencyTestingSection - AppId from URL query params:', currentAppId);
            updateAppId(currentAppId);
        }
    }, [urlOrgId, location.search, orgId, appId, getOrgId, getAppId]);

    // Checkbox and adequacy states
    const [selectedItems, setSelectedItems] = useState([]);
    const [adequacyDialogOpen, setAdequacyDialogOpen] = useState(false);
    const [adequacyStatus, setAdequacyStatus] = useState('');
    const [adequacyRemarks, setAdequacyRemarks] = useState('');

    // DA-NC data for table rows
    const [dancRecords, setDancRecords] = useState([]);

    // New Adequacy Check states for DA-NC integration
    const [adequacyChecks, setAdequacyChecks] = useState({
        'proficiency-testing': {
            status: '',
            remarks: '',
            isSubmitted: false,
            submittedAt: null
        }
    });

    // Clause Number states
    const [clauseNo, setClauseNo] = useState([]);
    const [selectedClause, setSelectedClause] = useState(null);

    // Instructions state
    const [instructions, setInstructions] = useState([]);
    const [instructionsLoading, setInstructionsLoading] = useState(false);

    // Refresh trigger for AdequacyCheckSection to reload data
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Unfreeze records state
    const [unfreezeRecords, setUnfreezeRecords] = useState([]);

    // Current user for DA NC system - get from auth context or session
    const [currentUser] = useState(() => {
        return localStorage.getItem('userName') ||
            localStorage.getItem('currentUser') ||
            'System User';
    });

    // Current user ID for DA NC system - get numeric ID for Insertedby field
    const [currentUserId] = useState(() => {
        return parseInt(localStorage.getItem('userId')) ||
            parseInt(localStorage.getItem('currentUserId')) ||
            0;
    });

    const getApplicationStage = useCallback(async (appId) => {
        try {
            if (!appId) return;

            console.log('🔄 ProficiencyTestingSection - Fetching application stage with appId:', appId);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
            const result = await response.json();

            if (result.success) {
                setApplicationStage(result.stage);
            } else {
                console.log('Stage not found, defaulting to stage 1 for demo purposes');
                setApplicationStage(1); // Default to 1 for demo purposes
            }
        } catch (error) {
            console.error('Error fetching application stage:', error);
            console.log('Error occurred, defaulting to stage 1 for demo purposes');
            setApplicationStage(1); // Default to 1 for demo purposes
        }
    }, []);

    // Handle adequacy check change with useCallback to prevent re-renders
    const handleAdequacyCheck = useCallback((sectionId, value) => {
        console.log(`Updating adequacy status for ${sectionId}:`, value);
        setAdequacyChecks(prev => {
            const updated = {
                ...prev,
                [sectionId]: {
                    ...prev[sectionId],
                    status: value,
                    lastUpdated: new Date().toISOString()
                }
            };
            console.log('Updated adequacyChecks:', updated);
            return updated;
        });
    }, []);

    // Handle adequacy remarks change with useCallback to prevent re-renders
    const handleAdequacyRemarks = useCallback((sectionId, remarks) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...prev[sectionId],
                remarks: remarks,
                lastUpdated: new Date().toISOString()
            }
        }));
    }, []);

    // Handle adequacy check submission with batch operations for multiple selected items
    const handleAdequacySubmit = async (sectionId) => {
        console.log('=== BATCH ADEQUACY SUBMIT CALLED ===');
        console.log('Selected items:', selectedItems);
        console.log('Current adequacyChecks state:', adequacyChecks);

        const adequacy = adequacyChecks[sectionId];
        console.log('Found adequacy data for section:', adequacy);

        if (!adequacy || !adequacy.status) {
            console.warn('No adequacy status to submit for section:', sectionId);
            return;
        }

        if (!selectedItems.length) {
            setError('Please select items to update adequacy status');
            return;
        }

        try {
            setSaving(true);
            setError(null);

            console.log(`Processing batch adequacy check for ${selectedItems.length} items`);

            // Categorize selected items by their current status
            const itemsToUpdate = [];
            const itemsToInsert = [];

            selectedItems.forEach(itemId => {
                const currentStatus = getItemStatus(itemId);
                if (currentStatus === 'Not Assessed') {
                    itemsToInsert.push(itemId);
                } else {
                    itemsToUpdate.push(itemId);
                }
            });

            console.log('Batch processing plan:', {
                itemsToUpdate: itemsToUpdate.map(id => ({ id, currentStatus: getItemStatus(id) })),
                itemsToInsert: itemsToInsert.map(id => ({ id, currentStatus: getItemStatus(id) })),
                newStatus: adequacy.status
            });

            let updatedCount = 0;
            let insertedCount = 0;
            const errors = [];

            // Process updates for existing records
            for (const itemId of itemsToUpdate) {
                try {
                    // Determine NCtype based on userTypeId
                    const userTypeId = localStorage.getItem('userTypeId');
                    let ncType = 'Document Adequacy'; // Default

                    if (userTypeId === '4') {
                        ncType = 'Document Adequacy';
                    } else if (userTypeId === '6') {
                        ncType = 'Assessment';
                    }

                    const updateData = {
                        subsectionName: sectionId,
                        informationStatus: adequacy.status,
                        observation: adequacy.status === 'Inadequate'
                            ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                            : 'Document adequacy check completed',
                        NCtype: ncType,
                        sourceid: itemId,
                        isactive: 1,
                        ClauseNo: selectedClause ? (typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')) : null,
                    };

                    console.log(`Updating existing record for item ${itemId}:`, updateData);
                    console.log('🔄 ProficiencyTestingSection - Updating DANC with orgId:', orgId, 'appId:', appId);

                    // Add assessmentId and assessorId for userType 6 (Assessment)
                    let response;
                    if (userTypeId === '6') {
                        response = await updateDANCBySourceId(
                            orgId,
                            appId,
                            'Proficiency Testing',
                            itemId,
                            assessmentId,
                            assessorId,
                            updateData
                        );
                    } else {
                        response = await updateDANCBySourceId(
                            orgId,
                            appId,
                            'Proficiency Testing',
                            itemId,
                            0,
                            localStorage.getItem('orgId'),
                            updateData
                        );
                    }

                    if (response.success) {
                        updatedCount++;
                        console.log(`✅ Updated record for item ${itemId}`);
                    } else {
                        throw new Error(`Failed to update item ${itemId}: ${response.message}`);
                    }
                } catch (error) {
                    console.error(`Error updating item ${itemId}:`, error);
                    errors.push(`Update failed for item ${itemId}: ${error.message}`);
                }
            }

            // Process inserts for new records
            for (const itemId of itemsToInsert) {
                try {
                    // Determine NCtype based on userTypeId
                    const userTypeId = localStorage.getItem('userTypeId');
                    const NCtype = userTypeId === '4' ? 'Document Adequacy' : 'Assessment';
                    console.log(`🔍 ProficiencyTesting - Creating DANC record for userType ${userTypeId} with NCtype: ${NCtype}`);

                    const dancData = {
                        Orgid: parseInt(orgId),
                        Appid: parseInt(appId),
                        SectionName: 'Proficiency Testing',
                        SubsectionName: sectionId,
                        InformationProvided: adequacy.remarks || '',
                        InformationStatus: adequacy.status,
                        Observation: adequacy.status === 'Inadequate'
                            ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                            : 'Document adequacy check completed',
                        sourceid: itemId,
                        Insertedby: currentUserId,
                        NCtype: NCtype,
                        ClauseNo: selectedClause ? (typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')) : null,
                        assessmentId: parseInt(assessmentId) || 0,
                        assessorId: parseInt(assessorId) || localStorage.getItem('orgId')
                    };

                    console.log(`Creating new record for item ${itemId}:`, dancData);
                    const response = await insertDANC(dancData);

                    if (response.success) {
                        insertedCount++;
                        console.log(`✅ Created record for item ${itemId}`);
                    } else {
                        throw new Error(`Failed to create item ${itemId}: ${response.message}`);
                    }
                } catch (error) {
                    console.error(`Error creating item ${itemId}:`, error);
                    errors.push(`Insert failed for item ${itemId}: ${error.message}`);
                }
            }

            // Mark as submitted locally
            setAdequacyChecks(prev => ({
                ...prev,
                [sectionId]: {
                    ...prev[sectionId],
                    ClauseNo: selectedClause ? (typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')) : '',
                    isSubmitted: true,
                    submittedAt: new Date().toISOString()
                }
            }));

            // Show results
            const successMessage = [];
            if (updatedCount > 0) successMessage.push(`${updatedCount} record(s) updated`);
            if (insertedCount > 0) successMessage.push(`${insertedCount} record(s) created`);

            if (errors.length > 0) {
                // setError(`Partial success: ${successMessage.join(', ')}. Errors: ${errors.join('; ')}`);
            } else {
                setSaveMessage(`Batch adequacy check completed successfully! ${successMessage.join(', ')} with status: ${adequacy.status}`);

                // Clear selected items after successful submission
                console.log('🧹 Clearing selected items after successful adequacy submission');
                setSelectedItems([]);
                loadSavedData(); // Refresh table data
            }

            // Refresh DA-NC data to update table display
            await loadDANCData();

            // Trigger refresh of AdequacyCheckSection component
            setRefreshTrigger(prev => prev + 1);

            // Clear success message after 5 seconds
            setTimeout(() => setSaveMessage(''), 5000);

        } catch (error) {
            console.error('=== ERROR in batch handleAdequacySubmit ===');
            console.error('Error:', error);

            setError('Failed to submit batch adequacy check: ' + error.message);

            // Revert submission status on error
            setAdequacyChecks(prev => ({
                ...prev,
                [sectionId]: {
                    ...prev[sectionId],
                    isSubmitted: false,
                    submittedAt: null
                }
            }));
        } finally {
            setSaving(false);
        }
    };

    // Load DA NC data for all sections
    const loadDANCData = useCallback(async () => {
        if (!orgId || !appId) return;

        try {
            // Determine NCtype based on userTypeId for filtering
            const userTypeId = localStorage.getItem('userTypeId');
            const NCtype = userTypeId === '6' ? 'Assessment' : 'Document Adequacy';
            console.log(`🔍 ProficiencyTesting - Loading DANC data for userType ${userTypeId} with expected NCtype: ${NCtype}`);

            // Get all DA NC records for this org and app
            const response = await getDANC({
                orgid: orgId,
                appid: appId,
                assessorId: assessorId,
                isactive: 1
            });

            console.log('DA-NC API Response:', response); // Debug log

            if (response.data.success && response.data.data && response.data.data.length > 0) {
                // Filter records based on userTypeId and NCtype
                const filteredRecords = response.data.data.filter(record => record.NCtype === NCtype);
                console.log(`🔍 ProficiencyTesting - Filtered records for NCtype "${NCtype}":`, filteredRecords.length);

                // Store Records for table row binding
                setDancRecords(filteredRecords);

                // Create adequacy checks from DA NC data
                const dancAdequacyChecks = {};

                filteredRecords.forEach(record => {

                    // Handle both SubsectionName and direct mapping from SectionName
                    let sectionKey = record.SubsectionName;

                    // If SubsectionName is not available, try to map from SectionName
                    if (!sectionKey && record.SectionName) {
                        const sectionMappings = {
                            'Proficiency Testing': 'proficiency-testing',
                            'PT Details': 'proficiency-testing',
                            'PT Provider': 'proficiency-testing'
                        };
                        sectionKey = sectionMappings[record.SectionName] || record.SectionName.toLowerCase().replace(/\s+/g, '-');
                    }

                    if (sectionKey) {
                        dancAdequacyChecks[sectionKey] = {
                            status: record.InformationStatus || '',
                            remarks: record.InformationProvided || record.Observation || '',
                            ClauseNo: record.ClauseNo || '',
                            isSubmitted: false, // Allow users to modify and resubmit
                            submittedAt: record.updatedate || record.Insertdate,
                            dancRecord: record // Store original record for reference
                        };

                        console.log(`Mapped Record to section "${sectionKey}":`, {
                            status: record.InformationStatus,
                            remarks: record.InformationProvided || record.Observation,
                            sourceid: record.sourceid
                        });
                    }
                });

                // Merge with existing adequacy checks
                setAdequacyChecks(prev => {
                    const merged = {
                        ...prev,
                        ...dancAdequacyChecks
                    };
                    console.log('Final merged adequacy checks:', merged);
                    return merged;
                });

                setSaveMessage(`${response.data.data.length} Records loaded and bound to adequacy checks`);
            } else {
                console.log('No Records found in response');
                setDancRecords([]);
            }
        } catch (error) {
            console.error('Error loading DA-NC data:', error);
            setError('Error loading DA-NC data: ' + error.message);
        }
    }, [orgId, appId]);

    // Load unfreeze records
    const loadUnfreezeRecords = useCallback(async () => {
        if (!appId) return;

        try {
            const response = await UnfreezeApiService.getUnfreezeRecords(appId, orgId);
            if (response.success) {
                console.log('Unfreeze records loaded:', response.data);
                setUnfreezeRecords(response.data);
            }
        } catch (err) {
            console.error("Load unfreeze records error:", err);
        }
    }, [appId, orgId]);

    // Helper function to check if a section is unfrozen based on API data
    const isSectionUnfrozen = (sectionTitle) => {
        if (!unfreezeRecords || unfreezeRecords.length === 0) return false;

        const today = new Date();
        // Reset time to start of day for date-only comparison
        today.setHours(0, 0, 0, 0);

        return unfreezeRecords.some(record => {
            if (record.sectionname !== sectionTitle) return false;

            const expiryDate = new Date(record.expirydate);
            // Reset expiry date time to start of day for date-only comparison
            expiryDate.setHours(0, 0, 0, 0);

            // Return true if expiry date is today or in the future (still valid for editing)
            // Compare dates only, not timestamps
            return expiryDate.getTime() >= today.getTime();
        });
    };

    // Fetch instructions from API
    useEffect(() => {
        const fetchInstructions = async () => {
            try {
                setInstructionsLoading(true);
                const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';
                const response = await fetch(`${API_BASE_URL}/instructions?sectionName=Proficiency Testing&labTypeId=1`);
                const data = await response.json();

                if (data.success && data.data.instructions) {
                    // Sort instructions by orderNo
                    const sortedInstructions = data.data.instructions.sort((a, b) => a.orderNo - b.orderNo);
                    setInstructions(sortedInstructions);
                }
            } catch (error) {
                console.error('Error fetching instructions:', error);
                // Fallback to default instructions if API fails
                setInstructions([]);
            } finally {
                setInstructionsLoading(false);
            }
        };

        fetchInstructions();
    }, []);

    // Fetch PT providers data on component mount
    useEffect(() => {
        const loadInitialData = async () => {
            getApplicationStage(appId);
            await fetchPTProviders();
            await fetchClauseNo();
            if (appId) {
                await loadSavedData();
                await loadDANCData();
                await loadUnfreezeRecords();
            }
        };

        loadInitialData();
    }, [appId, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchPTProviders = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch both NABL and other providers
            const [nablResponse, otherResponse] = await Promise.all([
                PTProviderService.getNABLPTProviders(),
                PTProviderService.getOtherPTProviders()
            ]);

            if (nablResponse.success) {
                setNablProviders(nablResponse.data);
            }

            if (otherResponse.success) {
                setOtherProviders(otherResponse.data);
            }

        } catch (error) {
            console.error('Error fetching PT providers:', error);
            setError('Failed to load PT provider data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch clause numbers from API
    const fetchClauseNo = useCallback(async () => {
        try {
            console.log('Fetching clause numbers for Lab Staff section...');

            const clauseResponse = await getClauseNo({ standard: '2017', feild: 'Testing' });

            if (clauseResponse.data?.success && clauseResponse.data?.data) {
                setClauseNo(clauseResponse.data.data);
                console.log('Loaded clause numbers:', clauseResponse.data.data.length);
            } else {
                console.warn('No clause numbers found or API returned success: false');
                setClauseNo([]);
            }
        } catch (error) {
            console.error('Error fetching clause numbers:', error);
            setClauseNo([]);
        }
    }, []);

    const loadSavedData = useCallback(async () => {
        try {
            setLoadingTable(true);

            const effectiveOrgId = orgId;
            const effectiveAppId = appId;

            console.log('📄 ProficiencyTestingSection - Loading PT provider data with appId:', effectiveAppId, 'orgId:', effectiveOrgId);
            const response = await PTProviderService.getPTProviderData(effectiveAppId, effectiveOrgId);
            if (response.success) {
                setSavedData(response.data || []);
                // Also reload DA-NC data to bind adequacy status
                await loadDANCData();
            }
        } catch (error) {
            console.error('Error loading saved PT provider data:', error);
        } finally {
            setLoadingTable(false);
            setSelectedItems([]); // Clear selections after data load
        }
    }, [appId, orgId, loadDANCData]);

    const clearForm = () => {
        setParticipationIn('0');
        setNodalLaboratory('');
        setNodalLaboratoryOthers('');
        setIlcDetails('');
        setRemarks('');
        setIsDropdownSelection(false);
        setExistingDataId(null);
        setEditingId(null);
        setSaveMessage('');
        setError(null);
    };

    const loadDataForEdit = (data) => {
        setEditingId(data.Id);
        setExistingDataId(data.Id);
        setParticipationIn(data.participationIn || '0');
        setNodalLaboratory(data.nodalLaboratory || '');
        setNodalLaboratoryOthers(data.nodalLaboratoryOthers || '');
        setIlcDetails(data.ilcDetails || '');
        setRemarks(data.remarks || '');
        // Check if remarks exists and is from predefined list
        setIsDropdownSelection(
            data.remarks &&
            scrutinyRemarks.some(remark => remark.ScrutinyRemark === data.remarks)
        );
    };

    // const getProviderName = (participationIn, nodalLaboratory, nodalLaboratoryOthers) => {
    //     if (participationIn === 'PT' && nodalLaboratory) {
    //         const provider = nablProviders.find(p => p.PT_Name === nodalLaboratory);
    //         return provider ? provider.PT_Name : 'Unknown Provider';
    //     } else if (participationIn === 'PT (Others)' && nodalLaboratoryOthers) {
    //         const provider = otherProviders.find(p => p.Name === nodalLaboratoryOthers);
    //         return provider ? provider.Name : 'Unknown Provider';
    //     }
    //     return 'N/A';
    // };

    const getProviderName = (participationIn, nodalLaboratory, nodalLaboratoryOthers) => {
        if (participationIn === 'PT' && nodalLaboratory) {
            const provider = nablProviders.find(p => p.PT_Name === nodalLaboratory);
            return provider ? provider.PT_Name : 'Unknown Provider';
        } else if (participationIn === 'PT (Others)' && nodalLaboratory) {
            const provider = nodalLaboratory;
            return provider ? provider : 'Unknown Provider';
        } else if (participationIn === 'IQC' && nodalLaboratory) {
            return nodalLaboratory ? nodalLaboratory : 'Unknown Provider';
        } 
        return 'N/A';
    };

    const handleParticipationChange = (event) => {
        setParticipationIn(event.target.value);
        // Reset nodal laboratory when participation changes
        setNodalLaboratory('');
        // Reset nodal laboratory others when participation changes
        setNodalLaboratoryOthers('');
        // Reset ILC details when participation changes
        setIlcDetails('');
        // Reset remarks when participation changes
        setRemarks('');
        // Reset dropdown selection flag
        setIsDropdownSelection(false);
        if (event.target.value !== "IQC") {
            setIqcType("");
        }
    };

    const handleNodalLaboratoryChange = (event) => {
        setNodalLaboratory(event.target.value);
    };

    const handleNodalLaboratoryOthersChange = (event) => {
        setNodalLaboratoryOthers(event.target.value);
    };

    const handleIlcDetailsChange = (event) => {
        setIlcDetails(event.target.value);
    };

    const handleRemarksChange = (event, newValue, reason) => {
        console.log('Remarks change:', { event, newValue, reason });

        if (reason === 'selectOption') {
            // User selected from dropdown
            setRemarks(newValue || '');
            setIsDropdownSelection(true);
        } else if (reason === 'clear') {
            // User cleared the selection
            setRemarks('');
            setIsDropdownSelection(false);
        } else {
            // Handle TextField input (when called from regular TextField)
            if (event && event.target) {
                console.log('User typed in remarks input:', event.target.value);
                setRemarks(event.target.value);
                setIsDropdownSelection(false); // Reset dropdown selection flag
            }
        }
    };

    const validateForm = () => {
        const errors = [];

        // Validate participation selection
        if (participationIn === '0') {
            errors.push('Please select a participation type');
            return errors;
        }

        // Validate fields based on participation type
        if (participationIn === 'PT' && nodalLaboratory === '0') {
            errors.push('Please select a Nodal laboratory/PT Provider for PT/EQA (NABL Accredited)');
        }

        if (participationIn === 'PT (Others)' && nodalLaboratoryOthers === '0') {
            errors.push('Please select a Nodal laboratory/PT Provider for PT (Others)');
        }

        if (participationIn === 'ILC' && !ilcDetails.trim()) {
            errors.push('Please enter Name and Certificate No, City of Participating Laboratory(ies) for ILC');
        }

        if (participationIn === 'Analysis of samples using split/blind of same samples by two persons/analyzers/two methods') {
            if (!remarks.trim()) {
                errors.push('Please enter remarks for Analysis of samples');
            }
        }

        if (participationIn === 'IQC' && !iqcType.trim()) {
            errors.push('Please select at least one IQC type');
        }

        return errors;
    };

    const handleSave = async () => {
        try {
            if (!appId) {
                setError('Application ID is required to save data');
                return;
            }

            // Validate form before saving
            const validationErrors = validateForm();
            if (validationErrors.length > 0) {
                setError(validationErrors.join(', '));
                return;
            }

            setSaving(true);
            setError(null);
            setSaveMessage('');

            const mainOrgid = localStorage.getItem('mainOrgId');
            const effectiveOrgId = mainOrgid || orgId;
            const effectiveAppId = appId;

            const dataToSave = {
                labTypeId: localStorage.getItem('labTypeId') || '1',
                id: existingDataId,
                appId: effectiveAppId,
                orgId: effectiveOrgId,
                participationIn,
                nodalLaboratory: participationIn === "IQC" ? iqcType || null : (nodalLaboratory !== '0' ? nodalLaboratory : null),
                nodalLaboratoryOthers: nodalLaboratoryOthers !== '0' ? nodalLaboratoryOthers : null,
                ilcDetails: ilcDetails.trim() || null,
                remarks: remarks.trim() || null,
                providerType: participationIn === 'PT' ? 'nabl' : participationIn === 'PT (Others)' ? 'other' : null
            };

            const response = await PTProviderService.savePTProviderData(dataToSave);

            if (response.success) {
                setSaveMessage('PT provider data saved successfully!');
                if (!existingDataId && response.data.id) {
                    setExistingDataId(response.data.id);
                }

                // Refresh the saved data table
                await loadSavedData();

                // Clear the form after successful save
                clearForm();

                // Clear success message after 3 seconds
                setTimeout(() => setSaveMessage(''), 3000);
            }

        } catch (error) {
            console.error('Error saving PT provider data:', error);
            setError('Failed to save PT provider data. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openDeleteDialog = (id, type) => {
        setDeleteItemId(id);
        setDeleteItemType(type);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setDeleteItemId(null);
        setDeleteItemType('');
    };

    // Helper function to get adequacy status for an item
    const getItemStatus = (itemId) => {
        const dancRecord = dancRecords.find(record =>
            record.sourceid && parseInt(record.sourceid) === parseInt(itemId)
        );
        return dancRecord ? dancRecord.InformationStatus : 'Not Assessed';
    };

    // Check if selection conflicts with business rules
    const isValidSelection = (currentSelection, newItemId, isAdding) => {
        if (!isAdding) return true; // Always allow deselection

        const newItemStatus = getItemStatus(newItemId);
        const currentStatuses = currentSelection.map(id => getItemStatus(id));

        // Check if current selection has Adequate or Inadequate
        const hasAdequate = currentStatuses.includes('Adequate');
        const hasInadequate = currentStatuses.includes('Inadequate');

        // Rule 1: Cannot select both Adequate and Inadequate
        if (newItemStatus === 'Adequate' && hasInadequate) {
            setError('Cannot select both Adequate and Inadequate items at the same time');
            return false;
        }
        if (newItemStatus === 'Inadequate' && hasAdequate) {
            setError('Cannot select both Adequate and Inadequate items at the same time');
            return false;
        }

        // Rule 2: Can select Adequate/Inadequate + Not Assessed (this is allowed)
        return true;
    };

    // Checkbox handlers
    const handleSelectItem = (itemId) => {
        setSelectedItems(prev => {
            const isCurrentlySelected = prev.includes(itemId);
            const isAdding = !isCurrentlySelected;

            // Validate selection before making changes
            if (!isValidSelection(prev, itemId, isAdding)) {
                return prev; // Return previous selection unchanged
            }

            const newSelection = isCurrentlySelected
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId];

            console.log('Selection changed:', {
                itemId,
                itemStatus: getItemStatus(itemId),
                previousSelection: prev,
                newSelection,
                selectionStatuses: newSelection.map(id => ({ id, status: getItemStatus(id) }))
            });

            // Clear error on successful selection
            if (isAdding) {
                setError(null);
            }

            return newSelection;
        });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedItems(savedData.map(item => item.Id));
        } else {
            setSelectedItems([]);
        }
    };

    // Adequacy popup handlers
    const closeAdequacyDialog = () => {
        setAdequacyDialogOpen(false);
        setAdequacyStatus('');
        setAdequacyRemarks('');
    };

    // Helper function to get adequacy status for a table row
    const getAdequacyStatusForRow = (rowId) => {
        console.log(`Looking for adequacy info for rowId: ${rowId}`);
        console.log('Available Records:', dancRecords);

        const dancRecord = dancRecords.find(record =>
            record.sourceid && parseInt(record.sourceid) === parseInt(rowId)
        );

        console.log(`Found Record for rowId ${rowId}:`, dancRecord);

        if (dancRecord) {
            return {
                status: dancRecord.InformationStatus,
                observation: dancRecord.Observation,
                informationProvided: dancRecord.InformationProvided,
                ClauseNo: dancRecord.ClauseNo,
                insertedDate: dancRecord.Insertdate,
                updatedDate: dancRecord.updatedate
            };
        }

        return null;
    };

    const handleDelete = async () => {
        try {
            setSaving(true);
            setError(null);

            const response = await PTProviderService.deletePTProviderData(existingDataId);

            if (response.success) {
                // Refresh the saved data table
                await loadSavedData();

                // Clear the form after successful deletion
                clearForm();
                setSaveMessage('PT provider data deleted successfully!');

                // Clear success message after 3 seconds
                setTimeout(() => setSaveMessage(''), 3000);
            }

        } catch (error) {
            console.error('Error deleting PT provider data:', error);
            setError('Failed to delete PT provider data. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        try {
            closeDeleteDialog();

            if (deleteItemType === 'form') {
                await handleDelete();
            } else if (deleteItemType === 'table') {
                await handleDeleteFromTable(deleteItemId);
            }
        } catch (error) {
            console.error('Error in confirmDelete:', error);
        }
    };

    const handleDeleteFromTable = async (id) => {
        try {
            setSaving(true);
            setError(null);

            const response = await PTProviderService.deletePTProviderData(id);

            if (response.success === true) {
                // Refresh the saved data table
                await loadSavedData();

                // Clear form if the deleted item was being edited
                if (editingId === id) {
                    clearForm();
                }

                setSaveMessage('PT provider data deleted successfully!');
                setTimeout(() => setSaveMessage(''), 3000);
            }

        } catch (error) {
            console.error('Error deleting PT provider data:', error);
            setError('Failed to delete PT provider data. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
      <>
        <Box sx={{ p: 3, height: "100%" }}>
          {/* Instructions Section */}
          <Paper
            sx={{
              mb: 3,
              p: 3,
              backgroundColor: "#e3f2fd",
              border: "1px solid #1976d2",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(25, 118, 210, 0.15)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#1976d2",
                fontWeight: 600,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              📋 Instructions
            </Typography>

            {instructionsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 2,
                }}
              >
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading instructions...
                </Typography>
              </Box>
            ) : instructions.length > 0 ? (
              <Box
                sx={{
                  pl: 2,
                  "& p": {
                    mb: 1,
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                    color: "#424242",
                  },
                }}
              >
                {instructions.map((instruction, index) => (
                  <Typography key={instruction.id} component="p">
                    {index + 1}. {instruction.instruction}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  pl: 2,
                  "& p": {
                    mb: 1,
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                    color: "#424242",
                  },
                }}
              >
                <Typography component="p">
                  i. Select the PT type from the dropdown, then choose the
                  provider from the list. For ILC, provide the requested
                  information as specified.
                </Typography>
                <Typography component="p">
                  ii. If the required value is not available in the Master List,
                  email the details to supportwp@nabl.qcin.org.
                </Typography>
                <Typography component="p">
                  iii. Map the selected PTs/ILCs to your scope on the Scope
                  page.
                </Typography>
                <Typography component="p">
                  iv. Upload the PT/ILC results as a single PDF on the Enclosure
                  page.
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Adequacy Check Section - Only show when items are selected */}
          {selectedItems.length > 0 && (
            <AdequacyCheckSection
              sectionId="proficiency-testing"
              title="Proficiency Testing"
              applicationStage={applicationStage}
              adequacyCheck={adequacyChecks["proficiency-testing"]}
              onAdequacyChange={handleAdequacyCheck}
              onRemarksChange={handleAdequacyRemarks}
              onSubmit={handleAdequacySubmit}
              orgId={orgId}
              appId={appId}
              currentUser={currentUser}
              currentUserId={currentUserId}
              refreshTrigger={refreshTrigger}
              selectedItems={selectedItems}
              dancRecords={dancRecords}
              clauseNo={clauseNo}
              selectedClause={selectedClause}
              onClauseChange={(clause) => setSelectedClause(clause)}
              userTypeId={localStorage.getItem("userTypeId")}
            />
          )}

          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            applicationStage === "18" ||
            applicationStage === "18b" ||
            isSectionUnfrozen("Proficiency Testing")) && (
            <>
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  borderRadius: 1,
                  mb: 3,
                  "& .MuiAccordionSummary-content": {
                    margin: "16px 0",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    🏢 PROFICIENCY TESTING/ INTERLABORATORY COMPARISON
                  </Typography>

                  {/* Unfrozen section indicator */}
                  {isSectionUnfrozen("Proficiency Testing") && (
                    <Chip
                      label="🔓 UNFROZEN"
                      size="small"
                      sx={{
                        bgcolor: "#ff9800",
                        color: "white",
                        fontWeight: "bold",
                        animation: "pulse 2s infinite",
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Unfrozen Sections Notification */}
              {isSectionUnfrozen("Proficiency Testing") && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    🔓 Sections Unfrozen for Editing
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    The Proficiency Testing section has been temporarily
                    unfrozen for editing. You can now modify records and
                    add/edit/delete entries.
                  </Typography>
                  <Box
                    sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}
                  >
                    <Chip
                      label="Proficiency Testing"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <Button size="small" onClick={fetchPTProviders}>
                      Retry
                    </Button>
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    required
                    error={participationIn === "0"}
                  >
                    <InputLabel id="participation-in-label">
                      Participation in
                    </InputLabel>
                    <Select
                      labelId="participation-in-label"
                      id="participation-in"
                      value={participationIn}
                      label="Participation in *"
                      onChange={handleParticipationChange}
                    >
                      <MenuItem value="0">----Select----</MenuItem>
                      <MenuItem value="PT">PT (NABL Accredited)</MenuItem>
                      <MenuItem value="ILC">ILC</MenuItem>
                      {/* <MenuItem value="Analysis of samples using split/blind of same samples by two persons/analyzers/two methods">
                                            Any Other
                                        </MenuItem> */}
                      <MenuItem value="PT (Others)">PT (Others)</MenuItem>
                      <MenuItem value="IQC">IQC</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                {participationIn === "PT" && (
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      required
                      error={nodalLaboratory === "0"}
                    >
                      <InputLabel id="nodal-laboratory-label">
                        Nodal laboratory/PT Provider(Accreditation Body/
                        Country)
                      </InputLabel>
                      <Select
                        labelId="nodal-laboratory-label"
                        id="nodal-laboratory"
                        value={nodalLaboratory}
                        label="Nodal laboratory/PT Provider(Accreditation Body/ Country) *"
                        onChange={handleNodalLaboratoryChange}
                        disabled={loading}
                      >
                        <MenuItem value="0">-- Select --</MenuItem>
                        {loading ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading...
                          </MenuItem>
                        ) : (
                          nablProviders.map((provider) => (
                            <MenuItem
                              key={provider.Id}
                              value={provider.PT_Name}
                            >
                              {provider.PT_Name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {participationIn === "PT (Others)" && (
                  <Grid item xs={12} md={6}>
                    {/* <FormControl fullWidth required error={nodalLaboratoryOthers === '0'}>
                                        <InputLabel id="nodal-laboratory-others-label">
                                            Nodal laboratory/PT Provider(Accreditation Body/ Country)
                                        </InputLabel>
                                        <Select
                                            labelId="nodal-laboratory-others-label"
                                            id="nodal-laboratory-others"
                                            value={nodalLaboratoryOthers}
                                            label="Nodal laboratory/PT Provider(Accreditation Body/ Country) *"
                                            onChange={handleNodalLaboratoryOthersChange}
                                            disabled={loading}
                                        >
                                            <MenuItem value="0">-Select-</MenuItem>
                                            {loading ? (
                                                <MenuItem disabled>
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                    Loading...
                                                </MenuItem>
                                            ) : (
                                                otherProviders.map((provider) => (
                                                    <MenuItem key={provider.ID} value={provider.Name}>
                                                        {provider.Name}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                     */}

                    <TextField
                      fullWidth
                      required
                      label=" Nodal laboratory/PT Provider(Accreditation Body/ Country)"
                      value={nodalLaboratory || ""}
                      onChange={(e) => setNodalLaboratory(e.target.value)}
                      variant="outlined"
                      error={!nodalLaboratory || nodalLaboratory === "0"}
                      helperText={
                        !nodalLaboratory ? "This field is required" : ""
                      }
                    />
                  </Grid>
                )}

                {participationIn === "ILC" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      id="ilc-details"
                      label="Name and Certificate No, City of Participating Laboratory(ies)"
                      value={ilcDetails}
                      onChange={handleIlcDetailsChange}
                      multiline
                      rows={3}
                      variant="outlined"
                      error={!ilcDetails.trim()}
                      helperText={
                        !ilcDetails.trim() ? "This field is required" : ""
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                )}

                {participationIn ===
                  "Analysis of samples using split/blind of same samples by two persons/analyzers/two methods" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      id="remarks"
                      label="Remarks"
                      value={remarks}
                      onChange={handleRemarksChange}
                      multiline
                      rows={3}
                      variant="outlined"
                      error={!remarks.trim()}
                      helperText={
                        !remarks.trim() ? "This field is required" : ""
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                )}
                {participationIn === "IQC" && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>IQC</InputLabel>

                      <Select
                        value={iqcType}
                        onChange={(e) => setIqcType(e.target.value)}
                        label="IQC"
                      >
                        <MenuItem value="">
                          <em>-- Select IQC Type --</em>
                        </MenuItem>

                        {iqcOptions.map((option, index) => (
                          <MenuItem
                            key={index}
                            value={option}
                            sx={{
                              whiteSpace: "normal",
                              py: 1.5,
                            }}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>

              {/* Save Button */}
              {(applicationStage === null ||
                applicationStage === "" ||
                applicationStage === "18a" ||
                isSectionUnfrozen("Proficiency Testing")) && (
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={
                      saving ||
                      !appId ||
                      (participationIn ===
                        "Analysis of samples using split/blind of same samples by two persons/analyzers/two methods" &&
                        !remarks.trim()) ||
                      (participationIn === "PT (Others)" &&
                        !nodalLaboratory.trim())
                    }
                    startIcon={saving ? <CircularProgress size={20} /> : null}
                  >
                    {saving ? "Saving..." : existingDataId ? "Update" : "Save"}
                  </Button>

                  {existingDataId && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={clearForm}
                      disabled={saving}
                    >
                      Cancel Edit
                    </Button>
                  )}
                </Box>
              )}

              {/* Saved Data Table */}
              <Divider sx={{ my: 4 }} />
            </>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              📊 PROFICIENCY TESTING/ INTERLABORATORY COMPARISON
            </Typography>
          </Box>

          {loadingTable ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : savedData.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "primary.light" }}>
                    {(applicationStage === "18" ||
                      applicationStage === "18b" ||
                      applicationStage === "20") &&
                      (localStorage.getItem("userTypeId") === "4" ||
                        localStorage.getItem("userTypeId") === "6") && (
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            textAlign: "center",
                            color: "white",
                            width: "50px",
                          }}
                        >
                          <Checkbox
                            checked={
                              selectedItems.length === savedData.length &&
                              savedData.length > 0
                            }
                            indeterminate={
                              selectedItems.length > 0 &&
                              selectedItems.length < savedData.length
                            }
                            onChange={handleSelectAll}
                            sx={{
                              color: "white",
                              "&.Mui-checked": {
                                color: "white",
                              },
                              "&.MuiCheckbox-indeterminate": {
                                color: "white",
                              },
                            }}
                          />
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              color: "white",
                              fontSize: "12px",
                            }}
                          >
                            Mark Adequacy{" "}
                          </Typography>
                        </TableCell>
                      )}
                    {(applicationStage === "18" ||
                      applicationStage === "18a" ||
                      applicationStage === "18b" ||
                      applicationStage === "20") && (
                      <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                        Adequacy Status
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                      Participation In
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                      Nodal Laboratory/PT Provider{" "}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                      ILC Details
                    </TableCell>
                    {/* <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Remarks</TableCell> */}

                    <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                      Created On
                    </TableCell>
                    {(applicationStage === null ||
                      applicationStage === "" ||
                      applicationStage === "18a" ||
                      isSectionUnfrozen("Proficiency Testing")) && (
                      <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {savedData.map((row) => (
                    <TableRow
                      key={row.Id}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "action.hover",
                        },
                        backgroundColor:
                          editingId === row.Id ? "warning.light" : "inherit",
                      }}
                    >
                      {(applicationStage === "18" ||
                        applicationStage === "18b" ||
                        applicationStage === "20") &&
                        (localStorage.getItem("userTypeId") === "4" ||
                          localStorage.getItem("userTypeId") === "6") && (
                          <TableCell>
                            <Checkbox
                              checked={selectedItems.includes(row.Id)}
                              onChange={() => handleSelectItem(row.Id)}
                              color="primary"
                            />
                          </TableCell>
                        )}
                      {(applicationStage === "18" ||
                        applicationStage === "18a" ||
                        applicationStage === "18b" ||
                        applicationStage === "20") && (
                        <TableCell>
                          {(() => {
                            const adequacyInfo = getAdequacyStatusForRow(
                              row.Id,
                            );
                            if (adequacyInfo) {
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      px: 2,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      textAlign: "center",
                                      minWidth: "80px",
                                      bgcolor:
                                        adequacyInfo.status === "Adequate"
                                          ? "#e8f5e8"
                                          : adequacyInfo.status === "Inadequate"
                                            ? "#ffebee"
                                            : adequacyInfo.status ===
                                                "To be Verify"
                                              ? "#fff3cd"
                                              : "#f5f5f5",
                                      color:
                                        adequacyInfo.status === "Adequate"
                                          ? "#2e7d32"
                                          : adequacyInfo.status === "Inadequate"
                                            ? "#c62828"
                                            : adequacyInfo.status ===
                                                "To be Verify"
                                              ? "#856404"
                                              : "#666",
                                      border: `1px solid ${
                                        adequacyInfo.status === "Adequate"
                                          ? "#a5d6a7"
                                          : adequacyInfo.status === "Inadequate"
                                            ? "#ef9a9a"
                                            : adequacyInfo.status ===
                                                "To be Verify"
                                              ? "#ffeaa7"
                                              : "#ddd"
                                      }`,
                                    }}
                                  >
                                    {adequacyInfo.status || "Not Assessed"}
                                  </Box>
                                  {adequacyInfo.status === "Inadequate" &&
                                    adequacyInfo.observation && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#e62300ff",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        "{adequacyInfo.observation}"
                                      </Typography>
                                    )}
                                  {adequacyInfo.ClauseNo && (
                                    <Chip
                                      label={`Clause: ${adequacyInfo.ClauseNo}`}
                                      size="small"
                                      sx={{
                                        fontSize: "0.625rem",
                                        height: 20,
                                        bgcolor: "#e3f2fd",
                                        color: "#1976d2",
                                        border: "1px solid #bbdefb",
                                      }}
                                    />
                                  )}
                                </Box>
                              );
                            } else {
                              return (
                                <Chip
                                  label="Not Assessed"
                                  size="small"
                                  sx={{
                                    bgcolor: "#6c757d",
                                    color: "white",
                                    fontSize: "0.75rem",
                                  }}
                                />
                              );
                            }
                          })()}
                        </TableCell>
                      )}
                      <TableCell>
                        {row.participationIn ===
                        "Analysis of samples using split/blind of same samples by two persons/analyzers/two methods"
                          ? "Any Other"
                          : row.participationIn}
                      </TableCell>
                      <TableCell>
                        {getProviderName(
                          row.participationIn,
                          row.nodalLaboratory,
                          row.nodalLaboratoryOthers,
                        )}
                      </TableCell>
                      <TableCell>
                        {row.ilcDetails ? (
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.ilcDetails.substring(0, 50)}
                            {row.ilcDetails.length > 50 ? "..." : ""}
                          </Typography>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      {/* <TableCell>
                                            {row.remarks ? (
                                                <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {row.remarks.substring(0, 50)}{row.remarks.length > 50 ? '...' : ''}
                                                </Typography>
                                            ) : 'N/A'}
                                        </TableCell> */}

                      <TableCell>
                        {new Date(row.CreatedOn).toLocaleDateString()}
                      </TableCell>
                      {(applicationStage === null ||
                        applicationStage === "" ||
                        applicationStage === "18a" ||
                        isSectionUnfrozen("Proficiency Testing")) && (
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => loadDataForEdit(row)}
                                disabled={saving}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  openDeleteDialog(row.Id, "table")
                                }
                                disabled={saving}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No PT provider data saved yet. Fill out the form above and click
              Save to add data.
            </Alert>
          )}
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={closeDeleteDialog}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete this PT provider data? This action
              cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} color="primary">
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={saving}
            >
              {saving ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Adequacy Check Dialog */}
        <Dialog
          open={adequacyDialogOpen}
          onClose={closeAdequacyDialog}
          maxWidth="sm"
          fullWidth
          aria-labelledby="adequacy-dialog-title"
        >
          <DialogTitle
            id="adequacy-dialog-title"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              fontWeight: 600,
            }}
          >
            ✅ Mark Adequacy Status
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body1"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Selected Items: <strong>{selectedItems.length}</strong>
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="adequacy-status-label">
                    Adequacy Status
                  </InputLabel>
                  <Select
                    labelId="adequacy-status-label"
                    value={adequacyStatus}
                    label="Adequacy Status"
                    onChange={(e) => setAdequacyStatus(e.target.value)}
                  >
                    <MenuItem value="adequate">Adequate</MenuItem>
                    <MenuItem value="inadequate">Inadequate</MenuItem>
                    <MenuItem value="To be Verify">To be Verify</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {adequacyStatus === "inadequate" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    label="Remarks"
                    placeholder="Please provide detailed remarks for inadequate status..."
                    value={adequacyRemarks}
                    onChange={(e) => setAdequacyRemarks(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
              )}

              {adequacyStatus === "adequate" && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <Typography variant="body2">
                      ✅ Selected items will be marked as{" "}
                      <strong>Adequate</strong>.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={closeAdequacyDialog}
              color="primary"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Update adequacyChecks state with the old dialog's values
                setAdequacyChecks((prev) => ({
                  ...prev,
                  "proficiency-testing": {
                    ...prev["proficiency-testing"],
                    status:
                      adequacyStatus === "adequate" ? "Adequate" : "Inadequate",
                    remarks: adequacyRemarks,
                    lastUpdated: new Date().toISOString(),
                  },
                }));
                // Then submit
                handleAdequacySubmit("proficiency-testing");
              }}
              color="success"
              variant="contained"
              disabled={saving || !adequacyStatus}
              startIcon={saving ? <CircularProgress size={20} /> : null}
              sx={{
                px: 3,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {saving ? "Updating..." : "Submit Adequacy"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
};

export default ProficiencyTestingSection;
